import { KeyIcon } from '@heroicons/react/24/outline';
import { Button, Pagination } from '@nextui-org/react';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Tooltip } from '@nextui-org/react';
import { Spinner } from '@nextui-org/spinner';
import lo from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  ClusterTag,
  PHPPort,
  ServerHost,
  ServerName,
  ServerSchema,
  ServerUUID,
  SSHPassword,
  SSHPort,
  SSHUsername,
  FrontendServerSchema,
} from '@common/types';
import { AddServer } from '@portal/components/AddServer';
import { InputSearchUI } from '@portal/components/common/InputSearch.ui';
import { NoteIconUI } from '@portal/components/common/NoteIcon.ui';
import { SelectClusterUI } from '@portal/components/common/SelectCluster.ui';
import { TableCountPageSizeSelectorUI } from '@portal/components/common/TableCountPageSizeSelector.ui';
import { TableNoDataUi } from '@portal/components/common/TableNoData.ui';
import { TerminalIconUI } from '@portal/components/common/TerminalIcon.ui';
import { TypographyUI } from '@portal/components/common/Typography.ui';
import { UnifiedNavigationUI } from '@portal/components/common/UnifiedNavigation.ui';
import { EditServer } from '@portal/components/EditServer';
import { SftpBrowser } from '@portal/components/SftpBrowser';
import { UserDropdown } from '@portal/components/UserDropdown';
import { useTerminalConnectionPool } from '@portal/context/TerminalConnectionPool';
import { tsrestClient } from '@portal/provider/tsrest';
import { calcTotalPage, paginate, useAuthStatus } from '@portal/utils';
import { ServersTableUI } from '@portal/views/servers/components/Servers.Table.ui';

import { BatchSslPreviewModal } from './components/BatchSslPreviewModal';
import { BatchSslResultsModal, BatchSslResultsData } from './components/BatchSslResultsModal';
import { SelectiveBatchSslModal } from './components/SelectiveBatchSslModal';
import { UpdateSslPasswordModal } from './components/UpdateSslPasswordModal';

const findServerFromUUID = (servers: ServerSchema[], uuid: ServerUUID) => servers.find(s => s.uuid === uuid);

const CLEAN_SERVER_SCHEMA = {
  uuid: '' as ServerUUID,
  name: '' as ServerName,
  clusterTag: '' as ClusterTag,
  host: '' as ServerHost,
  sshPort: 22 as SSHPort,
  phpPort: 80 as PHPPort,
  connectionType: 'persistent-ssh' as const,
  sshUsername: '' as SSHUsername,
  sshPassword: '' as SSHPassword,
} satisfies Partial<ServerSchema>;

const ServersContent = () => {
  const { openConnection, connectionPool } = useTerminalConnectionPool();
  const { canAccessAdminFeatures } = useAuthStatus();
  const [searchParams, setSearchParams] = useSearchParams();

  const [serverSearch, setServerSearch] = useState('');
  const [selectedCluster, setSelectedCluster] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const { data, isLoading, error, refetch, isFetched } = tsrestClient.servers.get.useQuery(['servers'], {
    headers: {
      authorization: localStorage.getItem('token') || '',
    },
  });
  const { data: statsData } = tsrestClient.serversStats.get.useQuery(
    ['servers-stats'],
    {
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    },
    {
      refetchInterval: 1000,
    }
  );
  const removeServerMutation = tsrestClient.servers.remove.useMutation({
    onError: (e, variables) => {
      const serverName = findServerFromUUID(data?.body.servers || [], variables.params.uuid as ServerUUID)?.name;
      toast(
        <>
          <TypographyUI className='font-bold'>Cannot delete server &apos;{serverName}&apos;</TypographyUI>
          <TypographyUI className='text-ts'>error: {(e.body as any).message}</TypographyUI>
        </>,
        { type: 'error' }
      );
      console.error(e);
    },
    onSuccess: (_, variables) => {
      const serverName = findServerFromUUID(data?.body.servers || [], variables.params.uuid as ServerUUID)?.name;
      toast(`Server '${serverName}' is successfully deleted`, { type: 'info' });
      refetch();
    },
  });
  const reinitializeServerStatsMutation = tsrestClient.serversStats.reinitialize.useMutation({
    onError: (e, variables) => {
      const serverName = findServerFromUUID(data?.body.servers || [], variables.params.uuid as ServerUUID)?.name;
      toast(
        <>
          <TypographyUI className='font-bold'>Cannot reinitialize server &apos;{serverName}&apos;</TypographyUI>
          <TypographyUI className='text-ts'>error: {(e.body as any).message}</TypographyUI>
        </>,
        { type: 'error' }
      );
      console.error(e);
    },
    onSuccess: (_, variables) => {
      const serverName = findServerFromUUID(data?.body.servers || [], variables.params.uuid as ServerUUID)?.name;
      toast(`Server '${serverName}' initialization is successfully triggered`, { type: 'info' });
      refetch();
    },
  });

  const reinitializeFailedServersMutation = tsrestClient.servers.serverAction.useMutation({
    onError: e => {
      toast(
        <>
          <TypographyUI className='font-bold'>Cannot reinitialize failed servers</TypographyUI>
          <TypographyUI className='text-ts'>error: {(e.body as any).message}</TypographyUI>
        </>,
        { type: 'error' }
      );
      console.error(e);
    },
    onSuccess: response => {
      const { summary } = response.body;
      toast(
        <>
          <TypographyUI className='font-bold'>Reinitialization completed</TypographyUI>
          <TypographyUI className='text-ts'>
            {summary.success} successful, {summary.failed} failed out of {summary.total} total
          </TypographyUI>
        </>,
        { type: 'info' }
      );
      refetch();
    },
  });
  const serverActionMutation = tsrestClient.servers.action.useMutation({
    onError: (e, variables) => {
      const serverName = findServerFromUUID(data?.body.servers || [], variables.params.uuid as ServerUUID)?.name;
      toast(
        <>
          <TypographyUI className='font-bold'>Cannot perform action on server &apos;{serverName}&apos;</TypographyUI>
          <TypographyUI className='text-ts'>error: {(e.body as any).message}</TypographyUI>
        </>,
        { type: 'error' }
      );
      console.error(e);
    },
    onSuccess: (e, variables) => {
      toast(e.body.message, { type: 'info' });
      refetch();

      // If reveal password is active and action was password update, refetch the password
      if (showSslPasswords && variables.body.action === 'updateSslPassword') {
        const server = data?.body.servers.find(s => s.uuid === variables.params.uuid);
        if (server) {
          refetchSslPasswords([server.uuid]);
        }
      }
    },
  });

  // Batch SSL Execute Mutation
  const batchSslExecuteMutation = tsrestClient.servers.clusterActionExecute.useMutation({
    onError: e => {
      toast(
        <>
          <TypographyUI className='font-bold'>Batch SSL update failed</TypographyUI>
          <TypographyUI className='text-ts'>error: {(e.body as any).message}</TypographyUI>
        </>,
        { type: 'error' }
      );
      console.error(e);
    },
    onSuccess: response => {
      const data = response.body as BatchSslResultsData;

      const successCount = data.summary.success;
      const failureCount = data.summary.failed;

      toast(`Batch SSL update completed: ${successCount} success, ${failureCount} failures`, { type: 'info' });

      // Show detailed results modal
      setBatchSslResultsData(data);
      setIsBatchSslResultsModalOpen(true);

      // Close both batch modals (regular and selective)
      closeBatchSslModal();
      closeSelectiveBatchSslModal();
      refetch();

      // If reveal password is active, refetch passwords for successfully updated servers
      if (showSslPasswords && data.results) {
        const successfulServerUUIDs = data.results.filter(r => r.success).map(r => r.serverUUID);
        if (successfulServerUUIDs.length > 0) {
          refetchSslPasswords(successfulServerUUIDs);
        }
      }
    },
  });

  useEffect(() => {
    if (!isFetched) return;
    setSearchParams({ cluster: selectedCluster, page: `${page}`, pageSize: `${pageSize}` });
    // Clear SSL passwords when cluster filter changes
    if (showSslPasswords) {
      setSslPasswords({});
      setSslPasswordLoading({});
      setSslPasswordErrors({});
    }
  }, [selectedCluster, page, pageSize, isFetched]);

  useEffect(() => {
    if (!isFetched) return;
    setPage(parseInt(searchParams.get('page') || '1'));
    setPageSize(parseInt(searchParams.get('pageSize') || '100'));
    setSelectedCluster(searchParams.get('cluster') || 'all');
  }, [isFetched]);

  const totalData = data?.body?.servers.length || 0;
  const filteredData = useMemo(() => {
    return lo(data?.body?.servers || [])
      .filter(s => (selectedCluster === 'all' ? true : s.clusterTag === selectedCluster))
      .filter(
        s =>
          lo.includes(s.name.toLowerCase(), serverSearch.toLowerCase()) ||
          lo.includes(s.clusterTag.toLowerCase(), serverSearch.toLowerCase()) ||
          lo.includes(s.host.toLowerCase(), serverSearch.toLowerCase()) ||
          lo.includes(s.sshPort.toString(), serverSearch.toLowerCase()) ||
          lo.includes(s.phpPort.toString(), serverSearch.toLowerCase()) ||
          lo.includes(s.connectionType?.toLowerCase(), serverSearch.toLowerCase()) ||
          lo.includes(s.sshUsername.toLowerCase(), serverSearch.toLowerCase())
      )
      .value();
  }, [data, serverSearch, selectedCluster]);
  const paginatedData = useMemo(() => paginate(filteredData, pageSize, page), [filteredData, pageSize, page]);
  const totalPage = useMemo(() => calcTotalPage(filteredData.length, pageSize), [filteredData.length, pageSize]);
  useEffect(() => {
    if (page > totalPage) setPage(totalPage);
  }, [page, totalPage]);

  const onRemoveServer = async (serverUUID: ServerUUID, onClose: () => void) =>
    removeServerMutation.mutate(
      {
        params: { uuid: serverUUID },
        headers: {
          authorization: localStorage.getItem('token') || '',
        },
      },
      {
        onSettled: () => {
          onClose();
        },
      }
    );
  const onReinitializeServer = async (serverUUID: ServerUUID) =>
    reinitializeServerStatsMutation.mutate({
      params: { uuid: serverUUID },
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    });
  const onRestartServer = async (serverUUID: ServerUUID) =>
    serverActionMutation.mutate({
      params: { uuid: serverUUID },
      body: { action: 'restart' },
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    });

  const [editServerData, setEditServerData] = useState<FrontendServerSchema>(
    CLEAN_SERVER_SCHEMA as FrontendServerSchema
  );
  const [sslPasswordModalData, setSslPasswordModalData] = useState<ServerSchema | null>(null);
  const [sslUsername, setSslUsername] = useState('');
  const [sslPassword, setSslPassword] = useState('');
  const [isSslModalOpen, setIsSslModalOpen] = useState(false);

  // SFTP State
  const [isSftpModalOpen, setIsSftpModalOpen] = useState(false);
  const [sftpServerData, setSftpServerData] = useState<FrontendServerSchema | null>(null);

  // Batch SSL Update State
  const [isBatchSslModalOpen, setIsBatchSslModalOpen] = useState(false);
  const [batchSslPreviewData, setBatchSslPreviewData] = useState<{
    impactedServers: Array<{
      uuid: ServerUUID;
      name: ServerName;
      host: ServerHost;
      clusterTag: ClusterTag;
    }>;
    totalServers: number;
    sslEnabledCount: number;
  } | null>(null);
  const [batchSslUsername, setBatchSslUsername] = useState('');
  const [batchSslPassword, setBatchSslPassword] = useState('');
  const [batchSslResultsData, setBatchSslResultsData] = useState<BatchSslResultsData | null>(null);
  const [isBatchSslResultsModalOpen, setIsBatchSslResultsModalOpen] = useState(false);

  // Selective Batch SSL Update State
  const [isSelectiveBatchSslModalOpen, setIsSelectiveBatchSslModalOpen] = useState(false);
  const [selectiveBatchSslPreviewData, setSelectiveBatchSslPreviewData] = useState<{
    impactedServers: Array<{
      uuid: ServerUUID;
      name: ServerName;
      host: ServerHost;
      clusterTag: ClusterTag;
    }>;
    totalServers: number;
    sslEnabledCount: number;
  } | null>(null);
  const [selectiveBatchSslUsername, setSelectiveBatchSslUsername] = useState('');
  const [selectiveBatchSslPassword, setSelectiveBatchSslPassword] = useState('');

  // SSL Password Reveal State
  const [showSslPasswords, setShowSslPasswords] = useState(false);
  const [sslPasswords, setSslPasswords] = useState<Record<ServerUUID, string | null>>({});
  const [sslPasswordLoading, setSslPasswordLoading] = useState<Record<ServerUUID, boolean>>({});
  const [sslPasswordErrors, setSslPasswordErrors] = useState<Record<ServerUUID, string>>({});

  const getSslPasswordsMutation = tsrestClient.servers.getSslPasswords.useMutation({
    onError: e => {
      console.error('Error fetching SSL passwords:', e);
    },
  });

  // Fetch SSL password for a single server
  const fetchSslPassword = async (serverUUID: ServerUUID, serverClusterTag: ClusterTag) => {
    // If cluster filter is active, only fetch for servers in the selected cluster
    if (selectedCluster !== 'all' && serverClusterTag !== selectedCluster) {
      return;
    }

    // Don't fetch if already loading or already fetched
    if (sslPasswordLoading[serverUUID] || sslPasswords[serverUUID] !== undefined) {
      return;
    }

    // Check if server has SSL enabled
    const isSslEnabled = statsData?.body.stats?.[serverUUID]?.stats?.services?.http_proxy?.active;
    if (!isSslEnabled) {
      // Mark as not SSL server
      setSslPasswords(prev => ({ ...prev, [serverUUID]: null }));
      return;
    }

    // Set loading state
    setSslPasswordLoading(prev => ({ ...prev, [serverUUID]: true }));

    try {
      const response = await getSslPasswordsMutation.mutateAsync({
        body: {
          serverUUIDs: [serverUUID],
        },
        headers: {
          authorization: localStorage.getItem('token') || '',
        },
      });

      if (response.status === 200 && response.body.passwords.length > 0) {
        const passwordData = response.body.passwords[0];
        setSslPasswords(prev => ({ ...prev, [serverUUID]: passwordData.password }));
        if (passwordData.error) {
          setSslPasswordErrors(prev => ({ ...prev, [serverUUID]: passwordData.error || 'Unknown error' }));
        }
      }
    } catch (error) {
      console.error(`Error fetching SSL password for server ${serverUUID}:`, error);
      setSslPasswordErrors(prev => ({ ...prev, [serverUUID]: 'Failed to retrieve password' }));
    } finally {
      setSslPasswordLoading(prev => {
        const newState = { ...prev };
        delete newState[serverUUID];
        return newState;
      });
    }
  };

  // Refetch SSL passwords for multiple servers (used after password updates)
  const refetchSslPasswords = async (serverUUIDs: ServerUUID[]) => {
    if (!showSslPasswords || serverUUIDs.length === 0) {
      return;
    }

    // Get server data to check cluster tags and SSL status
    const servers = data?.body.servers || [];
    const serversToRefetch = serverUUIDs
      .map(uuid => {
        const server = servers.find(s => s.uuid === uuid);
        if (!server) return null;

        // Check if server has SSL enabled
        const isSslEnabled = statsData?.body.stats?.[uuid]?.stats?.services?.http_proxy?.active;
        if (!isSslEnabled) {
          return null;
        }

        // Check cluster filter
        if (selectedCluster !== 'all' && server.clusterTag !== selectedCluster) {
          return null;
        }

        return { uuid, clusterTag: server.clusterTag };
      })
      .filter((s): s is { uuid: ServerUUID; clusterTag: ClusterTag } => s !== null);

    if (serversToRefetch.length === 0) {
      return;
    }

    // Clear existing passwords, loading, and error states for these servers
    setSslPasswords(prev => {
      const newState = { ...prev };
      serversToRefetch.forEach(({ uuid }) => {
        delete newState[uuid];
      });
      return newState;
    });

    setSslPasswordLoading(prev => {
      const newState = { ...prev };
      serversToRefetch.forEach(({ uuid }) => {
        delete newState[uuid];
      });
      return newState;
    });

    setSslPasswordErrors(prev => {
      const newState = { ...prev };
      serversToRefetch.forEach(({ uuid }) => {
        delete newState[uuid];
      });
      return newState;
    });

    // Use setTimeout to ensure state is cleared before fetching
    setTimeout(() => {
      serversToRefetch.forEach(({ uuid, clusterTag }) => {
        // Directly fetch without checking if already loaded
        setSslPasswordLoading(prev => ({ ...prev, [uuid]: true }));

        getSslPasswordsMutation
          .mutateAsync({
            body: {
              serverUUIDs: [uuid],
            },
            headers: {
              authorization: localStorage.getItem('token') || '',
            },
          })
          .then(response => {
            if (response.status === 200 && response.body.passwords.length > 0) {
              const passwordData = response.body.passwords[0];
              setSslPasswords(prev => ({ ...prev, [uuid]: passwordData.password }));
              if (passwordData.error) {
                setSslPasswordErrors(prev => ({ ...prev, [uuid]: passwordData.error || 'Unknown error' }));
              }
            }
          })
          .catch(error => {
            console.error(`Error refetching SSL password for server ${uuid}:`, error);
            setSslPasswordErrors(prev => ({ ...prev, [uuid]: 'Failed to retrieve password' }));
          })
          .finally(() => {
            setSslPasswordLoading(prev => {
              const newState = { ...prev };
              delete newState[uuid];
              return newState;
            });
          });
      });
    }, 0);
  };

  const onUpdateServer = async (server: ServerSchema) => {
    // Convert to frontend schema (omit encryption fields)
    const frontendServer: FrontendServerSchema = {
      uuid: server.uuid,
      name: server.name,
      clusterTag: server.clusterTag,
      host: server.host,
      sshPort: server.sshPort,
      phpPort: server.phpPort,
      connectionType: server.connectionType,
      sshUsername: server.sshUsername,
      sshPassword: server.sshPassword,
    };
    setEditServerData(frontendServer);
  };
  const clearServerData = () => setEditServerData(CLEAN_SERVER_SCHEMA as FrontendServerSchema);
  const onOpenServerTerminal = (server: ServerSchema) => {
    // Convert to frontend schema (omit encryption fields)
    const frontendServer: FrontendServerSchema = {
      uuid: server.uuid,
      name: server.name,
      clusterTag: server.clusterTag,
      host: server.host,
      sshPort: server.sshPort,
      phpPort: server.phpPort,
      connectionType: server.connectionType,
      sshUsername: server.sshUsername,
      sshPassword: server.sshPassword,
    };

    // Check if server is in failed state
    const serverStatus = statsData?.body.stats?.[server.uuid];
    const isHoppingEnabled = serverStatus?.state === 'errored' || serverStatus?.state === 'initializing-error';

    // Pass isHoppingEnabled to openConnection
    openConnection(frontendServer, { isHoppingEnabled });
  };

  const onOpenSftp = (server: ServerSchema) => {
    // Convert to frontend schema (omit encryption fields)
    const frontendServer: FrontendServerSchema = {
      uuid: server.uuid,
      name: server.name,
      clusterTag: server.clusterTag,
      host: server.host,
      sshPort: server.sshPort,
      phpPort: server.phpPort,
      connectionType: server.connectionType,
      sshUsername: server.sshUsername,
      sshPassword: server.sshPassword,
    };
    setSftpServerData(frontendServer);
    setIsSftpModalOpen(true);
  };

  // Convert connectionPool to terminal statuses for display
  const terminalStatuses = useMemo(() => {
    const statuses: { [key: ServerUUID]: 'none' | 'connected' | 'error' | 'connecting' | 'disconnected' } = {};
    connectionPool.forEach((connection, uuid) => {
      statuses[uuid] =
        connection.status === 'connected'
          ? 'connected'
          : connection.status === 'connecting'
            ? 'connecting'
            : connection.status === 'error'
              ? 'error'
              : 'disconnected';
    });
    return statuses;
  }, [connectionPool]);

  const onUpdateSslPassword = async (server: ServerSchema) => {
    setSslPasswordModalData(server);
    setSslUsername('adelia');
    setSslPassword('');
    setIsSslModalOpen(true);
  };

  const handleSslPasswordUpdate = async () => {
    if (!sslPasswordModalData || !sslPassword) {
      toast('Please fill in the password', { type: 'error' });
      return;
    }

    serverActionMutation.mutate({
      params: { uuid: sslPasswordModalData.uuid },
      body: {
        action: 'updateSslPassword',
        username: sslUsername,
        password: sslPassword,
      },
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    });

    setIsSslModalOpen(false);
    setSslPasswordModalData(null);
    setSslUsername('');
    setSslPassword('');
  };

  const closeSslModal = () => {
    setIsSslModalOpen(false);
    setSslPasswordModalData(null);
    setSslUsername('');
    setSslPassword('');
  };

  // Cluster Actions Handlers
  const handleReinitializeFailed = async () => {
    console.log('Reinitialize Failed Servers clicked');
    reinitializeFailedServersMutation.mutate({
      body: { action: 'reinitializeFailed' },
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    });
  };

  const handleBatchSslUpdate = async () => {
    console.log('Batch SSL Update clicked for cluster:', selectedCluster);

    try {
      // Call the preview API using tsrest client
      const response = await tsrestClient.servers.clusterAction.query({
        params: { clusterId: selectedCluster },
        query: { type: 'sslUpdatePreview' },
        headers: {
          authorization: localStorage.getItem('token') || '',
        },
      });

      if (response.status !== 200) {
        const errorBody = response.body as { message: string };
        toast(`Failed to get preview: ${errorBody.message}`, { type: 'error' });
        return;
      }

      const previewData = response.body as {
        impactedServers: Array<{
          uuid: ServerUUID;
          name: ServerName;
          host: ServerHost;
          clusterTag: ClusterTag;
        }>;
        totalServers: number;
        sslEnabledCount: number;
      };

      setBatchSslPreviewData(previewData);
      setBatchSslUsername('adelia'); // Default username
      setBatchSslPassword('');
      setIsBatchSslModalOpen(true);
    } catch (error) {
      console.error('Error getting batch SSL preview:', error);
      toast('Failed to get preview data', { type: 'error' });
    }
  };

  const closeBatchSslModal = () => {
    setIsBatchSslModalOpen(false);
    setBatchSslPreviewData(null);
    setBatchSslUsername('');
    setBatchSslPassword('');
  };

  const closeBatchSslResultsModal = () => {
    setIsBatchSslResultsModalOpen(false);
    setBatchSslResultsData(null);
  };

  // Selective Batch SSL Update Handlers
  const handleSelectiveBatchSslUpdate = async () => {
    console.log('Selective Batch SSL Update clicked for cluster:', selectedCluster);

    try {
      // Call the preview API using tsrest client
      const response = await tsrestClient.servers.clusterAction.query({
        params: { clusterId: selectedCluster },
        query: { type: 'sslUpdatePreview' },
        headers: {
          authorization: localStorage.getItem('token') || '',
        },
      });

      if (response.status !== 200) {
        const errorBody = response.body as { message: string };
        toast(`Failed to get preview: ${errorBody.message}`, { type: 'error' });
        return;
      }

      const previewData = response.body as {
        impactedServers: Array<{
          uuid: ServerUUID;
          name: ServerName;
          host: ServerHost;
          clusterTag: ClusterTag;
        }>;
        totalServers: number;
        sslEnabledCount: number;
      };

      setSelectiveBatchSslPreviewData(previewData);
      setSelectiveBatchSslUsername('adelia'); // Default username
      setSelectiveBatchSslPassword('');
      setIsSelectiveBatchSslModalOpen(true);
    } catch (error) {
      console.error('Error getting selective batch SSL preview:', error);
      toast('Failed to get preview data', { type: 'error' });
    }
  };

  const closeSelectiveBatchSslModal = () => {
    setIsSelectiveBatchSslModalOpen(false);
    setSelectiveBatchSslPreviewData(null);
    setSelectiveBatchSslUsername('');
    setSelectiveBatchSslPassword('');
  };

  // SSL Password Reveal Handler
  const handleToggleSslPasswordReveal = () => {
    if (showSslPasswords) {
      // Hide passwords - clear all state
      setShowSslPasswords(false);
      setSslPasswords({});
      setSslPasswordLoading({});
      setSslPasswordErrors({});
    } else {
      // Show passwords column - passwords will be fetched one-by-one as rows are rendered
      setShowSslPasswords(true);
    }
  };

  const tableStatus = TableNoDataUi(isLoading, error !== null, (data?.body.servers.length || 0) === 0);

  if (!canAccessAdminFeatures) {
    setTimeout(() => {
      window.location.href = '/monitors';
    }, 2000);
    return (
      <div className='flex w-full h-screen gap-4 items-center justify-center'>
        <div className='flex items-center gap-8'>
          <Spinner />
          <TypographyUI variant='h5' color='blue-gray'>
            Not authorized, redirecting to monitors
          </TypographyUI>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Navigation Header */}
      <div className='bg-white border-b border-gray-200 shadow-sm mb-4'>
        <div className='py-2'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-6'>
              <UnifiedNavigationUI />
              <div>
                <TypographyUI className='text-lg font-semibold' color='blue-gray'>
                  Servers
                </TypographyUI>
                <TypographyUI className='text-sm' color='gray'>
                  Here is a list of registered servers
                </TypographyUI>
              </div>
            </div>
            <div className='flex items-center gap-3'>
              <TerminalIconUI />
              <NoteIconUI />
              <Tooltip
                content={
                  selectedCluster === 'all'
                    ? 'Perform actions on all servers'
                    : 'Perform actions on all servers in cluster'
                }
                classNames={{
                  content: 'bg-[#F9E79F]',
                }}
              >
                <Dropdown>
                  <DropdownTrigger>
                    <Button variant='bordered' color='primary' size='sm' startContent={<KeyIcon className='h-4 w-4' />}>
                      {selectedCluster === 'all' ? 'Server Actions' : 'Cluster Actions'}
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    onAction={key => {
                      if (key === 'reinitialize-failed') {
                        handleReinitializeFailed();
                      } else if (key === 'batch-ssl-update') {
                        handleBatchSslUpdate();
                      } else if (key === 'selective-batch-ssl-update') {
                        handleSelectiveBatchSslUpdate();
                      } else if (key === 'reveal-ssl-password') {
                        handleToggleSslPasswordReveal();
                      }
                    }}
                    classNames={{
                      base: 'bg-transparent',
                      list: 'bg-transparent',
                    }}
                  >
                    {selectedCluster === 'all'
                      ? [
                          <DropdownItem
                            key='reinitialize-failed'
                            textValue='Reinitialize Failed Servers'
                            className='bg-blue-50 hover:bg-blue-100 hover:shadow-md transition-all duration-200 text-gray-800 rounded-lg m-0 data-[hover=true]:bg-blue-100 data-[hover=true]:shadow-md'
                          >
                            Reinitialize Failed Servers
                          </DropdownItem>,
                          <DropdownItem
                            key='reveal-ssl-password'
                            textValue={showSslPasswords ? 'Hide SSL Passwords' : 'Reveal SSL Passwords'}
                            className='bg-emerald-50 hover:bg-emerald-100 hover:shadow-md transition-all duration-200 text-gray-800 rounded-lg m-0 data-[hover=true]:bg-emerald-100 data-[hover=true]:shadow-md'
                          >
                            {showSslPasswords ? 'Hide SSL Passwords' : 'Reveal SSL Passwords'}
                          </DropdownItem>,
                        ]
                      : [
                          <DropdownItem
                            key='batch-ssl-update'
                            textValue='Batch Update Password SSL'
                            className='bg-violet-50 hover:bg-violet-100 hover:shadow-md transition-all duration-200 text-gray-800 rounded-lg m-0 data-[hover=true]:bg-violet-100 data-[hover=true]:shadow-md'
                          >
                            Batch Update Password SSL
                          </DropdownItem>,
                          <DropdownItem
                            key='selective-batch-ssl-update'
                            textValue='Selective Batch Update Password SSL'
                            className='bg-cyan-50 hover:bg-cyan-100 hover:shadow-md transition-all duration-200 text-gray-800 rounded-lg m-0 data-[hover=true]:bg-cyan-100 data-[hover=true]:shadow-md'
                          >
                            Selective Batch Update Password SSL
                          </DropdownItem>,
                          <DropdownItem
                            key='reveal-ssl-password'
                            textValue={showSslPasswords ? 'Hide SSL Passwords' : 'Reveal SSL Passwords'}
                            className='bg-emerald-50 hover:bg-emerald-100 hover:shadow-md transition-all duration-200 text-gray-800 rounded-lg m-0 data-[hover=true]:bg-emerald-100 data-[hover=true]:shadow-md'
                          >
                            {showSslPasswords ? 'Hide SSL Passwords' : 'Reveal SSL Passwords'}
                          </DropdownItem>,
                        ]}
                  </DropdownMenu>
                </Dropdown>
              </Tooltip>
              <SelectClusterUI
                value={selectedCluster}
                clusterOption={data?.body.cluster || []}
                onSelectedClusterChange={setSelectedCluster}
              />
              <InputSearchUI onSearchChange={setServerSearch} placeholder={'Search Server'} />
              <AddServer />
              <UserDropdown />
            </div>
          </div>
        </div>
      </div>

      <div className='flex w-full gap-4 flex-col'>
        <div className='flex flex-wrap justify-center gap-2'>
          <TableCountPageSizeSelectorUI
            totalData={totalData}
            totalFilteredData={filteredData.length}
            selectedPageSize={pageSize}
            setPageSize={setPageSize}
          />
          <ServersTableUI
            paginatedData={paginatedData}
            tableStatus={tableStatus}
            onRemoveServer={onRemoveServer}
            onUpdateServer={onUpdateServer}
            onReinitializeServer={onReinitializeServer}
            onRestartServer={onRestartServer}
            onOpenServerTerminal={onOpenServerTerminal}
            onOpenSftp={onOpenSftp}
            onUpdateSslPassword={onUpdateSslPassword}
            serverStats={statsData?.body.stats}
            terminalStatuses={terminalStatuses}
            showSslPasswords={showSslPasswords}
            sslPasswords={sslPasswords}
            sslPasswordLoading={sslPasswordLoading}
            sslPasswordErrors={sslPasswordErrors}
            onFetchSslPassword={fetchSslPassword}
            selectedCluster={selectedCluster}
          />
          <Pagination isCompact showControls showShadow page={page} total={totalPage} onChange={setPage} />
        </div>
      </div>
      <EditServer serverData={editServerData} clearServerData={clearServerData} />
      <UpdateSslPasswordModal
        isOpen={isSslModalOpen}
        onOpenChange={closeSslModal}
        server={sslPasswordModalData}
        username={sslUsername}
        password={sslPassword}
        onUsernameChange={setSslUsername}
        onPasswordChange={setSslPassword}
        onSubmit={handleSslPasswordUpdate}
      />
      <BatchSslPreviewModal
        isOpen={isBatchSslModalOpen}
        onOpenChange={closeBatchSslModal}
        clusterId={selectedCluster}
        impactedServers={batchSslPreviewData?.impactedServers || []}
        totalServers={batchSslPreviewData?.totalServers || 0}
        sslEnabledCount={batchSslPreviewData?.sslEnabledCount || 0}
        username={batchSslUsername}
        password={batchSslPassword}
        onUsernameChange={setBatchSslUsername}
        onPasswordChange={setBatchSslPassword}
        onConfirm={() => {
          // Execute the batch SSL update
          batchSslExecuteMutation.mutate({
            params: { clusterId: selectedCluster },
            body: {
              action: 'sslUpdate',
              username: batchSslUsername,
              password: batchSslPassword,
            },
            headers: {
              authorization: localStorage.getItem('token') || '',
            },
          });
        }}
        isLoading={batchSslExecuteMutation.isLoading}
      />
      <BatchSslResultsModal
        isOpen={isBatchSslResultsModalOpen}
        onOpenChange={closeBatchSslResultsModal}
        resultsData={batchSslResultsData}
      />
      <SelectiveBatchSslModal
        isOpen={isSelectiveBatchSslModalOpen}
        onOpenChange={closeSelectiveBatchSslModal}
        clusterId={selectedCluster}
        availableServers={selectiveBatchSslPreviewData?.impactedServers || []}
        totalServers={selectiveBatchSslPreviewData?.totalServers || 0}
        sslEnabledCount={selectiveBatchSslPreviewData?.sslEnabledCount || 0}
        username={selectiveBatchSslUsername}
        password={selectiveBatchSslPassword}
        onUsernameChange={setSelectiveBatchSslUsername}
        onPasswordChange={setSelectiveBatchSslPassword}
        onConfirm={(mode, selectedServerUUIDs, serverPasswords) => {
          // Execute the selective batch SSL update based on mode
          if (mode === 'single') {
            batchSslExecuteMutation.mutate({
              params: { clusterId: selectedCluster },
              body: {
                action: 'sslUpdateSelective',
                username: selectiveBatchSslUsername,
                password: selectiveBatchSslPassword,
                serverUUIDs: selectedServerUUIDs,
              },
              headers: {
                authorization: localStorage.getItem('token') || '',
              },
            });
          } else {
            // Per-server mode
            batchSslExecuteMutation.mutate({
              params: { clusterId: selectedCluster },
              body: {
                action: 'sslUpdateSelectivePerServer',
                username: selectiveBatchSslUsername,
                serverPasswords: serverPasswords || [],
              },
              headers: {
                authorization: localStorage.getItem('token') || '',
              },
            });
          }
        }}
        isLoading={batchSslExecuteMutation.isLoading}
      />
      <SftpBrowser
        isOpen={isSftpModalOpen}
        onClose={() => {
          setIsSftpModalOpen(false);
          setSftpServerData(null);
        }}
        server={sftpServerData}
      />
    </>
  );
};

export const Servers = ServersContent;
